export default class ChannelTree {

  private _id: number;
  private _parentId: number;
  private _children: Array<ChannelTree>;
  private _name: string | undefined;
  private _nameLocalized: string | undefined;
  private _description: string | undefined;
  private _createdDate: string | undefined;
  private _modifiedDate: string | undefined;
  private _visibility = true;
  private _orderedParentChannelNames: Array<string> = [];
  private _referenceKey: string | undefined;
  private _reducedReferenceKey: string | undefined;
  private _reducedAltReferenceKey: string | undefined;
  private _rootedReferenceKey: string | undefined;
  private _referenceKeys: Array<string> = [];

  constructor(id: number) {
    this._id = id;
    this._parentId = 0;
    this._children = [];
  }

  public clone(): ChannelTree {

    const channelTreeClone = new ChannelTree(this.id);
    channelTreeClone.parentId = this.parentId;
    channelTreeClone.name = this.name;
    channelTreeClone._nameLocalized = this._nameLocalized;
    channelTreeClone._description = this._description;
    channelTreeClone._createdDate = this._createdDate;
    channelTreeClone._modifiedDate = this._modifiedDate;
    channelTreeClone._visibility = this._visibility;
    channelTreeClone._orderedParentChannelNames = this._orderedParentChannelNames.slice();
    channelTreeClone._referenceKey = this._referenceKey;
    channelTreeClone._reducedReferenceKey = this._reducedReferenceKey;
    channelTreeClone._reducedAltReferenceKey = this._reducedAltReferenceKey;
    channelTreeClone._rootedReferenceKey = this.rootedReferenceKey;
    channelTreeClone._referenceKeys = [...this._referenceKeys];
    
    for(const channelTreeChild of this.children) {
      channelTreeClone.children.push(channelTreeChild.clone());
    }
    return channelTreeClone;
  }

  public cloneSubTree(channelId: number): ChannelTree | undefined {
    const subTree = this.getSubTreeReference(channelId);
    if(!subTree) return undefined;
    const relativeDepthLevel = this.getRelativeDepthLevel(channelId);
    const clonedSubTree = subTree.clone();
    clonedSubTree.parentId = 0;
    if(relativeDepthLevel > 0) {
      clonedSubTree.adjustParentNames(relativeDepthLevel);
    }
    return clonedSubTree;
  }

  public getChannelIdByName(channelName: string): number | undefined {
    if(this.name === channelName) return this.id;
    for(const channelTreeChild of this.children) {
      const id = channelTreeChild.getChannelIdByName(channelName);
      if(id) return id;
    }
    return undefined;
  }

  public hasParent(): boolean {
    return this.parentId !== 0;
  }
  public hasChildren(): boolean {
    return this.children.length > 0;
  }

  public get id(): number {
    return this._id;
  }
  public set id(value: number) {
    this._id = value;
  }

  public get parentId(): number {
    return this._parentId;
  }
  public set parentId(value: number) {
    this._parentId = value;
  }
  
  public get children(): Array<ChannelTree> {
    return this._children;
  }
  public set children(value: Array<ChannelTree>) {
    this._children = value;
  }
  public get name(): string {
    return this._name ? this._name : '';
  }
  public set name(value) {
    if(value === this._name) return;
    this._name = value;
    this.updateReferenceKeys();
  }
  public get nameLocalized() {
    return this._nameLocalized ? this._nameLocalized : this.name;
  }
  public set nameLocalized(value) {
    this._nameLocalized = value;
  }
  public get description() {
    return this._description;
  }
  public set description(value) {
    this._description = value;
  }
  public get createdDate() {
    return this._createdDate;
  }
  public set createdDate(value) {
    this._createdDate = value;
  }
  public get modifiedDate() {
    return this._modifiedDate;
  }
  public set modifiedDate(value) {
    this._modifiedDate = value;
  }
  public get visibility() {
    return this._visibility;
  }
  public set visibility(value) {
    this._visibility = value;
  }

  public get orderedParentChannelNames() {
    return this._orderedParentChannelNames;
  }

  public set orderedParentChannelNames(value) {
    this._orderedParentChannelNames = value;
    this.updateReferenceKeys();
  }

  private updateReferenceKeys() {
    if(this._orderedParentChannelNames.length === 0) {
      this._rootedReferenceKey = this._name;
      this._referenceKey = this._name;
    }
    else {
      this._rootedReferenceKey = this._orderedParentChannelNames.join(':') + ':' + this._name;
      const parentsNoRoot = this._orderedParentChannelNames.slice(1).join(':');
      this._referenceKey = parentsNoRoot
        ? parentsNoRoot + ':' + this._name
        : this._name;
    }
    this.setReferenceKeys();
  }

  public get rootedReferenceKey() {
    return this._rootedReferenceKey ? this._rootedReferenceKey : '';
  }

  public get referenceKey() {
    return this._referenceKey ? this._referenceKey : '';
  }

  public get reducedReferenceKey() {
    return this._reducedReferenceKey ? this._reducedReferenceKey : '';
  }

  public get reducedAltReferenceKey() {
    return this._reducedAltReferenceKey ? this._reducedAltReferenceKey : '';
  }

  public removeChildren(childIds: Array<number>) {
    this.children = this.children.filter(child => !childIds.includes(child.id));
  }

  public initLocalizedNames(localizedChannelNameFunc: (keys: Array<string>) => string) {
    this.nameLocalized = localizedChannelNameFunc(this._referenceKeys);
    for(const subChannelTree of this.children) {
      subChannelTree.initLocalizedNames(localizedChannelNameFunc);
    }
  }

  private getSubTreeReference(channelId: number): ChannelTree | undefined {
    if(this.id === channelId) return this;
    for(const channelTreeChild of this.children) {
      const subTree = channelTreeChild.getSubTreeReference(channelId);
      if(subTree) return subTree;
    }
    return undefined;
  }

  private getRelativeDepthLevel(channelId: number, currentDepthLevel = 0): number {
    if(this.id === channelId) return currentDepthLevel;
    for(const channelTreeChild of this.children) {
      const result = channelTreeChild.getRelativeDepthLevel(channelId, currentDepthLevel + 1);
      if(result > 0) return result;
    }
    return -1;
  }

  private adjustParentNames(depthLevel: number) {
    this.orderedParentChannelNames = this.orderedParentChannelNames.length <= depthLevel
      ? []
      : this.orderedParentChannelNames.slice(depthLevel);
    for(const channelTreeChild of this.children) {
      channelTreeChild.adjustParentNames(depthLevel);
    }
  }

  private setReferenceKeys() {

    const reduceKey = (key: string) => {
      return key
      .replaceAll(' / ', '_')
      .replaceAll(':', '.')
      .replaceAll(/([^\d]*)( {0,2}[\d]{2,} {0,2})([^\d]*)/g, (match, p1, p2, p3) => p1 + p3)
      .replaceAll(' ', '_')
      .replaceAll('™', '');
    }

    this._referenceKeys = [];
    if(this.referenceKey) {
      this._referenceKeys.push(this.referenceKey);
      this._reducedReferenceKey = reduceKey(this.referenceKey);
      this._reducedAltReferenceKey = this._reducedReferenceKey.replaceAll('.', ':');
      if(this._reducedReferenceKey !== this.referenceKey) {
        this._referenceKeys.push(this._reducedReferenceKey);
      }
    }
    if(this.name) {
      this._referenceKeys.push(this.name);
      const reducedKey = reduceKey(this.name);
      if(reducedKey !== this.name) {
        this._referenceKeys.push(reducedKey);
      }
    }
  }

}
